/**
 * Erkennt Fehler, deren Ursache die Verbindung des Nutzers ist — nicht die App.
 *
 * Ohne diese Einordnung landen Funkloch, Firewall und blockiertes reCAPTCHA im
 * generischen „Unbekannter Fehler"-Zweig: Der Nutzer bekommt keinen brauchbaren
 * Hinweis, und der AI-Issue-Bot bekommt Netzstörungen als vermeintliche Bugs
 * gemeldet.
 *
 * Reine Logik ohne Framework-Imports, damit sie in `npm run test:unit` läuft.
 */

export type ConnectivityErrorKind = 'offline' | 'app-check' | null;

export interface ConnectivityErrorInfo {
  kind: Exclude<ConnectivityErrorKind, null>;
  header: string;
  message: string;
}

/**
 * Backend nicht erreichbar. Firestore meldet `unavailable`, wenn es den
 * WebChannel nicht aufbauen kann („Could not reach Cloud Firestore backend"),
 * und geht in den Offline-Modus.
 */
const OFFLINE_CODES = new Set([
  'unavailable',
  'deadline-exceeded',
  'firestore/unavailable',
  'firestore/deadline-exceeded',
  'functions/unavailable',
  'functions/deadline-exceeded',
  'auth/network-request-failed',
  'auth/timeout',
]);

/**
 * App Check bekam kein Token. Häufigste Ursachen: Tracking-Schutz oder
 * Privatmodus verwirft den `_GRECAPTCHA`-Cookie, ein Adblocker oder eine
 * Firewall blockiert `google.com/recaptcha`, oder das Script kommt bei
 * schlechtem Empfang nicht durch.
 */
const APP_CHECK_CODE_FRAGMENTS = ['appcheck/', 'app-check'];

const OFFLINE_MESSAGE_FRAGMENTS = [
  'could not reach cloud firestore backend',
  'client is offline',
  'failed to fetch',
  'network error',
  'networkerror',
  'load failed',
];

function normalize(value: unknown): string {
  return typeof value === 'string' ? value.toLowerCase() : '';
}

/**
 * Ordnet einen Fehler einer Verbindungsursache zu — oder `null`, wenn es ein
 * echter Anwendungsfehler ist.
 *
 * @param code Fehlercode (z.B. `unavailable`, `appCheck/recaptcha-error`)
 * @param message Fehlermeldung, als Rückfallebene wenn kein Code vorliegt
 * @param isOnline `navigator.onLine`; `undefined`, wenn nicht ermittelbar
 */
export function classifyConnectivityError(
  code: unknown,
  message: unknown,
  isOnline?: boolean,
): ConnectivityErrorInfo | null {
  const normalizedCode = normalize(code);
  const normalizedMessage = normalize(message);

  if (
    APP_CHECK_CODE_FRAGMENTS.some((fragment) =>
      normalizedCode.includes(fragment),
    )
  ) {
    return {
      kind: 'app-check',
      header: 'Sicherheitsprüfung fehlgeschlagen',
      message:
        'Eine Sicherheitsprüfung konnte in diesem Browser nicht durchgeführt werden. ' +
        'Bitte prüfen Sie Ihre Internetverbindung und laden Sie die Seite neu. ' +
        'Falls das nicht hilft: anderer Browser (Chrome oder Edge, kein privates Fenster) ' +
        'oder Tracking-Schutz für diese Seite deaktivieren.',
    };
  }

  const looksOffline =
    OFFLINE_CODES.has(normalizedCode) ||
    OFFLINE_MESSAGE_FRAGMENTS.some((fragment) =>
      normalizedMessage.includes(fragment),
    ) ||
    // Ein gemeldetes `offline` des Browsers ist für sich genommen Grund genug:
    // Jeder Fehler in diesem Zustand ist mit hoher Wahrscheinlichkeit Folge davon.
    isOnline === false;

  if (looksOffline) {
    return {
      kind: 'offline',
      header: 'Keine Verbindung',
      message:
        'Die Verbindung zum Server ist unterbrochen. Bitte prüfen Sie Ihre ' +
        'Internetverbindung und versuchen Sie es erneut. Ihre Daten sind nicht verloren.',
    };
  }

  return null;
}
